import { Button, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { type FormEvent, useCallback, useReducer, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { t } from '../../locale/texts';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { fhhAutocompleteMapper } from '../common/autocomplete/utils/AutocompleteMapper';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { storeLastSearchFhh } from '../common/lastSearch';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { trackSearchClick } from '../common/tracking';
import type { AutocompleteOption } from '../types';
import { BedroomFilter } from './BedroomFilter';
import { FhhHiddenFields } from './FhhHiddenFields';
import styles from './FhhSearch.module.css';
import actions, { reducer } from './fhhActions';
import { fhhDefaultState } from './fhhDefaultState';
import { useFhhInitialState } from './hooks/useFhhInitialState';
import { useFhhValidation } from './hooks/useFhhValidation';
import { buildFhhTrackingItem } from './utils/buildFhhTrackingItem';
import { formatBedroomText } from './utils/formatBedroomText';
import { generateGooglePlacesSessionToken } from './utils/googlePlacesSessionToken';

interface BedroomFilters {
    no_of_bedrooms_from?: number;
    no_of_beds_from?: number;
}

interface FhhSearchProps {
    prefill?: Partial<typeof fhhDefaultState>;
    position: string;
}

export const FhhSearch = ({ prefill, position }: FhhSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, fhhDefaultState);
    const [googlePlacesSessionToken] = useState(generateGooglePlacesSessionToken);

    const { fhhAutocompleteUrl, fhhResultUrl } = useConfig();

    useFhhInitialState(dispatch, prefill);
    const { validateState, invalid } = useFhhValidation(state, pristine);

    const handleFilterChange = useCallback(
        (filters: BedroomFilters) => {
            if (filters.no_of_bedrooms_from !== undefined) {
                dispatch(actions.setNumOfBedrooms(filters.no_of_bedrooms_from));
            }
            if (filters.no_of_beds_from !== undefined) {
                dispatch(actions.setNumOfBeds(filters.no_of_beds_from));
            }
        },
        [dispatch],
    );

    const handleDestinationSelect = useCallback(
        (option: AutocompleteOption | null, typedText?: string) => {
            if (!option || option.id === 'default') {
                dispatch(actions.allFieldsChanged(fhhDefaultState));
            } else {
                dispatch(actions.setDestination(option));
            }

            if (!option && typedText) {
                dispatch(actions.setRawDestination(typedText));
            }
        },
        [dispatch],
    );

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        if (validateState()) {
            storeLastSearchFhh(state);
            trackSearchClick('Search holiday rentals', '/reise/feriehus', 'holiday_rental', position, buildFhhTrackingItem(state));
        } else {
            setPristine(false);
            event.preventDefault();
        }
    };

    return (
        <SearchPanelWrapper
            className={styles['form-body']}
            onSubmit={handleSubmit}
            action={fhhResultUrl}
            method="GET"
            dataTestId="fhhSearch">
            <Autocomplete
                vertical="fhh"
                id="destination"
                label={t('fhh.destination.label')}
                placeholder={t('fhh.destination.placeholder')}
                defaultValue={state.destination}
                url={fhhAutocompleteUrl}
                headers={{ 'x-gtoken': googlePlacesSessionToken }}
                mapOptions={fhhAutocompleteMapper}
                error={invalid.destination ? t('fhh.validation.destination') : ''}
                showDefaultOptionOnFocus
                onSelect={handleDestinationSelect}
            />
            <FrontpageDatePickerWrapper
                startDate={state.startDate}
                endDate={state.endDate}
                useRange
                onDateChange={(newDates) => dispatch(actions.setDates(newDates))}>
                <TextField
                    id="date-fhh-checkin"
                    label={t('fhh.checkin.label')}
                    invalid={invalid.startDate}
                    data-testid="date-fhh-checkin"
                />
                <TextField
                    id="date-fhh-checkout"
                    label={t('fhh.checkout.label')}
                    invalid={invalid.endDate}
                    data-testid="date-fhh-checkout"
                />
            </FrontpageDatePickerWrapper>
            <BedroomFilter
                trigger={{
                    text: formatBedroomText(state),
                    label: 'Reisende',
                }}
                filters={{ no_of_beds_from: state.numOfBeds, no_of_bedrooms_from: state.numOfBedrooms }}
                onFilterChanged={handleFilterChange}
            />
            <FhhHiddenFields
                destinationData={state.destinationData}
                numOfBedrooms={state.numOfBedrooms}
                numOfBeds={state.numOfBeds}
                startDate={state.startDate}
                endDate={state.endDate}
            />
            <Button
                className={cn(styles['search-button'], {
                    [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                })}
                fullWidth
                type="submit"
                primary
                data-testid="fhhSearchButton">
                {t('action.search')}
            </Button>
        </SearchPanelWrapper>
    );
};
